/**
 * The outbox: writes made without a server, kept until one answers.
 *
 * An enqueue commits the row and its local echo in one transaction, so a
 * crash cannot leave a saved intent without its visible text or the reverse.
 * Rows for one entity replay in `seq` order; a later queued write with the
 * same method and path replaces the earlier one (at the newest seq), a
 * delete drops everything queued for its entity (and cancels the entity
 * outright if its create was queued and never sent), and a row behind a
 * queued or failed create remembers that it depends on it. In-flight rows are
 * never touched here.
 */

import { currentAccountId, lastAccountId } from './account';
import { broadcastInvalidated } from './cache';
import { applyEchoes, localStoreOf, queuedRowsIn, revisionKey } from './echo';
import { withEntityLock } from './entityLock';
import { getOutboxState, patchOutboxState } from './outboxState';
import { captureScope, listOutbox, listOutboxWithoutSession, withStores } from './store';
import { writePolicyFor } from './writePolicies';

import type { OfflineTx, OutboxRow, OutboxState, WriteScope } from './store';
import type { WritePolicy, WriteRequest } from './writePolicies';

/** Thrown when a write could not be persisted; the caller must report failure, not "saved". */
export class OutboxUnavailableError extends Error {
  /** Build the error with the one message every caller shows. */
  constructor() {
    super('Offline store unavailable: the change was not saved on this device');
    this.name = 'OutboxUnavailableError';
  }
}

/** One queued write, in the words the pending-changes UI shows. */
export interface PendingChange {
  id: string;
  entityKey: string;
  label: string;
  state: OutboxState;
  createdAt: number;
  lastError?: string;
  lastStatus?: number;
}

/** What a discard did, and whether it refused. */
interface DiscardResult {
  removedDependants: number;
  refused?: 'inflight';
}

/** The discard transaction's answer: the result plus the keys it dropped. */
interface DiscardOutcome extends DiscardResult {
  touched?: string[];
}

let scheduleFlush: (trigger: string) => void = () => undefined;

/** The flusher registers itself so a retry can kick a replay without a module cycle. */
export function registerOutboxFlushScheduler(fn: (trigger: string) => void): void {
  scheduleFlush = fn;
}

/** A fresh row id; `randomUUID` where the browser has it, a timestamp otherwise. */
const newId = (): string =>
  globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random().toString(36).slice(2)}`;

/** True for a row whose policy declares it the write that creates the entity. */
const isCreate = (row: OutboxRow): boolean =>
  row.method === 'POST' &&
  Boolean(writePolicyFor({ method: 'POST', url: row.path, body: row.body })?.createsEntity);

/** The request a row replays. */
const requestOf = (row: Pick<OutboxRow, 'method' | 'path' | 'body'>): WriteRequest => ({
  method: row.method,
  url: row.path,
  body: row.body,
});

/**
 * The words a queued write is shown in: its policy's, or the bare request
 * when no policy matches.
 *
 * The one place the rule lives, so the pending list and the rows a schema
 * upgrade parked in `meta.orphanedOutbox` never describe the same write two
 * different ways.
 */
export function labelForRow(row: Pick<OutboxRow, 'method' | 'path' | 'body'>): string {
  const req = requestOf(row);
  return writePolicyFor(req)?.describe?.(req) ?? `${row.method} ${row.path}`;
}

/** Recount pending and failed rows for the signed-in account and publish them, unless the account changed meanwhile. */
export async function refreshCounts(): Promise<void> {
  const account = currentAccountId();
  const rows = account ? await listOutbox(account) : [];
  if (currentAccountId() !== account) return;
  patchOutboxState({
    pendingCount: rows.filter(r => r.state !== 'failed').length,
    failedCount: rows.filter(r => r.state === 'failed').length,
    version: getOutboxState().version + 1,
  });
}

/** Whether the entity has rows waiting; while it does, its cached copy is the truth. */
export async function hasQueuedRows(entityKey: string): Promise<boolean> {
  const account = currentAccountId();
  if (!account) return false;
  return (await listOutbox(account)).some(r => r.entityKey === entityKey);
}

/** Rows waiting for the account that was last signed in; for the login wall, which has no session. */
export async function pendingCountForLastAccount(): Promise<number> {
  const account = lastAccountId();
  if (!account) return 0;
  return (await listOutboxWithoutSession(account)).filter(r => r.state !== 'failed').length;
}

/**
 * Undo each row's echo, inside the caller's transaction: its policy's
 * `undoLocally`, else delete every key it touched. Returns the keys touched,
 * deduplicated, so the caller can tell their screens.
 */
async function undoRows(tx: OfflineTx<'readwrite'>, rows: OutboxRow[]): Promise<string[]> {
  const touched = new Set<string>();
  for (const row of rows) {
    const req = requestOf(row);
    const policy = writePolicyFor(req);
    const keys = policy?.touches?.(req) ?? [];
    keys.forEach(k => touched.add(k));
    // Bound to a local first: `await policy?.undoLocally?.(…)` awaits
    // `undefined` for a row without one, and an await with no store request
    // pending lets IndexedDB commit the transaction early.
    const undo = policy?.undoLocally;
    if (undo) await undo(localStoreOf(tx), req);
    else for (const key of keys) await tx.objectStore('resources').delete(key);
  }
  return [...touched];
}

/**
 * Drop what a superseding write (a delete) replaces, inside its transaction.
 *
 * Returns the keys the cancelled rows touched when the entity never reached
 * the server — its create was queued and never sent — in which case the
 * caller queues nothing at all and the cancelled rows' own echoes are undone
 * (each by its own policy, not by the delete's `touches`). Null when the
 * entity may exist on the server and the delete must be queued.
 */
async function applySupersede(
  tx: OfflineTx<'readwrite'>,
  existing: OutboxRow[],
  entityKey: string
): Promise<string[] | null> {
  const cancelled = existing.filter(r => r.state !== 'inflight');
  const neverSent = existing.some(r => r.state === 'queued' && r.attempts === 0 && isCreate(r));
  for (const r of cancelled) await tx.objectStore('outbox').delete(r.id);
  if (!neverSent) return null;
  // The entity never reached the server: nothing to delete there.
  const touched = await undoRows(tx, cancelled);
  await tx.objectStore('meta').delete(revisionKey(entityKey));
  return touched;
}

/** The row to persist: a coalesced row keeps its id and birthday but takes the newest seq. */
function buildRow(
  req: WriteRequest,
  entityKey: string,
  seq: number,
  reuse: OutboxRow | undefined,
  create: OutboxRow | undefined
): OutboxRow {
  return {
    id: reuse?.id ?? newId(),
    seq,
    entityKey,
    method: req.method,
    path: req.url,
    body: req.body,
    state: 'queued',
    attempts: 0,
    createdAt: reuse?.createdAt ?? Date.now(),
    ...(create ? { dependsOnCreate: create.id } : {}),
  };
}

/** What `enqueue`'s transaction produced: the row, or the keys a cancelled entity's echoes touched. */
type EnqueueOutcome = { row: OutboxRow; cancelled?: never } | { row: null; cancelled: string[] };

/**
 * Queue a write and echo it into the cache, atomically, for a scope captured
 * when the write started. Callers hold the entity lock.
 *
 * Returns the persisted row; null when the write cancelled the entity (a
 * delete of a never-sent create) and there is nothing to replay — the
 * cancelled echoes' keys are then announced as discarded, here and in every
 * other tab, once the transaction has committed. Throws
 * `OutboxUnavailableError` when nothing was persisted.
 */
export async function enqueue(
  scope: WriteScope,
  req: WriteRequest,
  policy: WritePolicy
): Promise<OutboxRow | null> {
  const entityKey = policy.entity(req);
  let outcome: EnqueueOutcome | null = null;
  try {
    outcome = await withStores(scope, 'readwrite', async (tx): Promise<EnqueueOutcome> => {
      const outbox = tx.objectStore('outbox');
      const existing = await queuedRowsIn(tx, entityKey);
      const seq = (await outbox.getAll()).reduce((m, r) => Math.max(m, r.seq), 0) + 1;

      const cancelled = policy.supersedes ? await applySupersede(tx, existing, entityKey) : null;
      if (cancelled) return { row: null, cancelled };

      const reuse =
        policy.coalesce === 'last-wins' && !policy.supersedes
          ? existing.find(
              r => r.state === 'queued' && r.method === req.method && r.path === req.url
            )
          : undefined;
      if (reuse) await outbox.delete(reuse.id);

      // `existing` was read before the supersede deleted the queued rows, so a
      // superseding write must never take a dependency out of it: the create it
      // would name is already gone, and nothing would ever clear the reference.
      const create =
        policy.createsEntity || policy.supersedes
          ? undefined
          : existing.find(r => r.state !== 'inflight' && isCreate(r));
      const row = buildRow(req, entityKey, seq, reuse, create);
      await outbox.put(row);
      if (policy.applyLocally) await policy.applyLocally(localStoreOf(tx), req);
      return { row };
    });
  } catch {
    throw new OutboxUnavailableError();
  }
  if (!outcome) throw new OutboxUnavailableError();
  await refreshCounts();
  if (outcome.cancelled) broadcastInvalidated(outcome.cancelled, 'discarded');
  return outcome.row;
}

/**
 * A row update. Spelled out rather than `Partial<…>` because an explicit
 * `undefined` is meaningful here — it clears the field — which
 * `exactOptionalPropertyTypes` forbids for a plain optional property.
 */
export type OutboxRowPatch = { [K in keyof Omit<OutboxRow, 'id'>]?: OutboxRow[K] | undefined };

/** Change a row's state or bookkeeping fields, for a scope. `undefined` in the patch clears a field. */
export async function markRow(scope: WriteScope, id: string, patch: OutboxRowPatch): Promise<void> {
  await withStores(scope, 'readwrite', async tx => {
    const outbox = tx.objectStore('outbox');
    const row = await outbox.get(id);
    if (!row) return;
    const next = { ...row, ...patch } as Record<string, unknown>;
    for (const k of Object.keys(next)) if (next[k] === undefined) delete next[k];
    await outbox.put(next as unknown as OutboxRow);
  });
  await refreshCounts();
}

/** Clear `dependsOnCreate` on every row that was waiting behind an acknowledged create. */
async function clearDependants(
  tx: OfflineTx<'readwrite'>,
  row: OutboxRow,
  id: string
): Promise<void> {
  const outbox = tx.objectStore('outbox');
  for (const dependant of await queuedRowsIn(tx, row.entityKey)) {
    if (dependant.dependsOnCreate !== id) continue;
    const { dependsOnCreate: _dependsOnCreate, ...rest } = dependant;
    await outbox.put(rest as OutboxRow);
  }
}

/**
 * Remove an acknowledged (or resolved) row and repair what depended on it.
 *
 * In one transaction: delete the row; if it was the entity's create, clear
 * `dependsOnCreate` on the rows behind it (they may now carry an expected
 * revision); record the reply's revision as the threaded one when there is
 * one (kept even when the entity drains, until a fresh read supersedes it);
 * re-apply the remaining echoes, handing the acknowledged row's echo the
 * reply so the cached copy can carry the new revision.
 */
export async function removeRow(
  scope: WriteScope,
  id: string,
  threadRevision?: string | null,
  reply?: unknown
): Promise<void> {
  await withStores(scope, 'readwrite', async tx => {
    const outbox = tx.objectStore('outbox');
    const row = await outbox.get(id);
    if (!row) return;
    await outbox.delete(id);
    if (isCreate(row)) await clearDependants(tx, row, id);
    const remaining = await queuedRowsIn(tx, row.entityKey);
    if (threadRevision) {
      await tx.objectStore('meta').put({ key: revisionKey(row.entityKey), value: threadRevision });
    }
    if (reply !== undefined) {
      // Bound to a local first: `await policy?.applyLocally?.(…)` awaits
      // `undefined` when no policy echoes this write, and an await with no
      // store request pending lets IndexedDB commit the transaction early.
      const req = requestOf(row);
      const apply = writePolicyFor(req)?.applyLocally;
      if (apply) await apply(localStoreOf(tx), req, reply);
    }
    await applyEchoes(tx, remaining);
  });
  await refreshCounts();
}

/** The pending list in the policies' words, oldest first. */
export async function listPending(): Promise<PendingChange[]> {
  const account = currentAccountId();
  if (!account) return [];
  return (await listOutbox(account)).map(row => ({
    id: row.id,
    entityKey: row.entityKey,
    label: labelForRow(row),
    state: row.state,
    createdAt: row.createdAt,
    ...(row.lastError ? { lastError: row.lastError } : {}),
    ...(row.lastStatus ? { lastStatus: row.lastStatus } : {}),
  }));
}

/** Put a failed row back in the queue, forgetting its failure but not how often it was sent, and kick a replay. */
export async function retry(id: string): Promise<void> {
  const scope = captureScope();
  if (!scope) return;
  const rows = await listOutbox(scope.account);
  const target = rows.find(r => r.id === id);
  if (!target) return;
  await withEntityLock(target.entityKey, async () => {
    await withStores(scope, 'readwrite', async tx => {
      const outbox = tx.objectStore('outbox');
      const row = await outbox.get(id);
      if (!row || row.state === 'inflight') return;
      const { lastError: _lastError, lastStatus: _lastStatus, nextAttemptAt: _next, ...rest } = row;
      await outbox.put({ ...rest, state: 'queued' });
    });
  });
  await refreshCounts();
  scheduleFlush('retry');
}

/** Remove a row and its dependants inside one transaction, undoing their echoes and reporting the keys they touched. */
async function discardIn(tx: OfflineTx<'readwrite'>, id: string): Promise<DiscardOutcome> {
  const outbox = tx.objectStore('outbox');
  const current = await outbox.get(id);
  if (!current) return { removedDependants: 0 };
  if (current.state === 'inflight') return { removedDependants: 0, refused: 'inflight' };
  const all = await outbox.getAll();
  const removing = [current, ...all.filter(r => r.dependsOnCreate === id)];
  for (const r of removing) await outbox.delete(r.id);
  const touched = await undoRows(tx, removing);
  if ((await queuedRowsIn(tx, current.entityKey)).length === 0) {
    await tx.objectStore('meta').delete(revisionKey(current.entityKey));
  }
  return { removedDependants: removing.length - 1, touched };
}

/**
 * Drop a row the user gave up on.
 *
 * Under the entity lock, and inside one transaction: removes it and every
 * queued row that depended on it, undoes their echoes (each policy's
 * `undoLocally`, else deleting every cached key it touched), and forgets the
 * threaded revision if nothing remains. Refuses an in-flight row. Emits an
 * invalidation per touched key, here and in every other tab, so a screen
 * can refetch; the read path then rebuilds "server data + remaining echoes".
 */
export async function discard(id: string): Promise<DiscardResult> {
  const scope = captureScope();
  if (!scope) return { removedDependants: 0 };
  const rows = await listOutbox(scope.account);
  const target = rows.find(r => r.id === id);
  if (!target) return { removedDependants: 0 };
  const result = await withEntityLock(target.entityKey, () =>
    withStores(scope, 'readwrite', tx => discardIn(tx, id))
  );
  await refreshCounts();
  if (!result) return { removedDependants: 0 };
  broadcastInvalidated(result.touched ?? [], 'discarded');
  const { touched: _touched, ...rest } = result;
  return rest;
}
